import AiAssistantIcon from "@shared/svg/ai-assistant.svg?react";
import type { Asset } from "@shared/types/asset";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import TextEditor from "@shared/ui/TextEditor";
import { getMachineThumbnail } from "@shared/utils/getMachineThumbnail";
import React from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

const AssetCard = ({
  asset,
  buttonStyle,
  showCreateRequest,
  assetType,
  isSharedPage,
  className = "",
  onClickCreateRequest = () => {},
  sharedAssistant = null,
  qrCodeInfo = null,
  onAiChatClick,
  createRequestSlot = null,
}: {
  asset: Asset;
  assetType: string;
  showCreateRequest: boolean;
  buttonStyle: React.CSSProperties;
  isSharedPage?: boolean;
  onClickCreateRequest?: () => void;
  className?: string;
  sharedAssistant?: {
    _id: string;
    name: string;
  };
  qrCodeInfo?: React.ReactNode;
  /** Custom CTA rendered in place of the default Create Request button. */
  createRequestSlot?: React.ReactNode;
  /** Optional callback for AI chat click. When provided, replaces default navigation. */
  onAiChatClick?: (assistantId: string, assistantName: string) => void;
}) => {
  const { messages } = useIntl();
  const productThumbnail = getMachineThumbnail(asset, true);
  const navigate = useNavigate();

  return (
    <div
      className={`flex flex-col ${className}`}
      style={{
        width: "280px",
        ...(isSharedPage ? { transform: "translateY(-40px)" } : {}),
      }}
    >
      <div
        className="sticky gap-y-lg"
        style={{ top: isSharedPage ? "170px" : "110px" }}
      >
        <div className="flex flex-col p-2xl gap-y-2xl shadow-lg rounded-lg border border-solid border-primary bg-inverse">
          <div className="flex flex-col gap-y-lg">
            <div className="relative">
              <img
                src={productThumbnail}
                alt={asset.name}
                style={{ width: "232px", height: "232px" }}
                className="border border-primary border-solid rounded-lg object-contain relative"
              />
            </div>
            <div className="flex flex-col gap-y-2xs">
              <Headline size={HEADLINE_SIZES.SMALL} className="break-words">
                {asset.name}
              </Headline>
              <BodyText
                size={BODY_TEXT_SIZES.X_SMALL}
                color="text-secondary"
                className="break-words"
              >
                SN# {asset.serialNumber}
              </BodyText>
            </div>
            <div className="flex gap-x-sm">
              {createRequestSlot}
              {showCreateRequest && (
                <Button
                  style={buttonStyle}
                  text={
                    messages?.["sharedAssets"]?.["createRequest"] ||
                    "Create Request"
                  }
                  className="whitespace-nowrap w-1/2 flex-1"
                  onClick={() => {
                    onClickCreateRequest?.();
                  }}
                />
              )}
              {sharedAssistant && (
                <Button
                  variant={BUTTON_VARIANTS.OUTLINE}
                  leadingIcon={<AiAssistantIcon />}
                  onClick={() => {
                    if (onAiChatClick) {
                      onAiChatClick(sharedAssistant._id, sharedAssistant.name);
                    } else {
                      navigate("/app/ai/chat", {
                        state: {
                          assistantId: sharedAssistant._id,
                        },
                      });
                    }
                  }}
                />
              )}
              {qrCodeInfo && qrCodeInfo}
            </div>
            <div
              className={`flex w-full h-px border-0 border-b border-solid border-primary ${
                showCreateRequest || createRequestSlot ? "!my-3xl" : "!my-md"
              }`}
            />
            <div className="flex flex-col gap-y-xs">
              <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
                {messages["sharedAssets"]["assetType"]}
              </BodyText>
              <BodyText size={BODY_TEXT_SIZES.X_SMALL}>{assetType}</BodyText>
            </div>
            {isSharedPage && (
              <div className="flex flex-col gap-y-xs">
                <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
                  {messages?.["sharedAssets"]?.["lastUpdated"] ||
                    "Last Updated"}
                </BodyText>
                <BodyText size={BODY_TEXT_SIZES.X_SMALL}>
                  {new Date(
                    asset?.updatedAt ??
                      asset.updated_at ??
                      asset?.createdAt ??
                      asset.created_at,
                  )
                    .toLocaleDateString("en-GB")
                    .replace(/\//g, "-")}
                </BodyText>
              </div>
            )}
            {asset.description && (
              <div className="flex flex-col gap-y-xs">
                <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
                  {messages?.["common"]?.["description"] || "Description"}
                </BodyText>
                <TextEditor
                  content={asset.description}
                  keyId="notetaker-summary-closed"
                  className="description-wrapper"
                  readOnly
                  maxHeight="max-h-9xl new-scrollbar"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetCard;
